import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { Div } from 'client/widget-components/basicComponents';
import { Customization } from 'client/widget-components/ecom/stores/types';
import {
    changeChoiceQuantity,
    setChoiceSelected,
} from 'client/widget-components/ecom/stores/checkboxValueUtils';
import { mergeResponsiveStyles } from 'client/widget-components/responsive-styles/responsiveStylesService';
import React, { FC, memo, useCallback } from 'react';
import { CUSTOMIZATIONS_DATA_GRABS } from '../../constants';
import { ProductCustomizationsStyles } from '../../types';
import BaseProductCustomizationLabel from '../BaseProductCustomizationLabel';
import CustomizationFieldError from '../CustomizationFieldError';
import CheckboxCustomizationChoices from './CheckboxCustomizationChoices';
import ChoicesLimitMessage from './ChoicesLimitMessage';
import { getChoicesLimitMessage } from './choicesLimitUtils';

interface CheckboxCustomizationProps extends Customization {
    styles?: ProductCustomizationsStyles;
    value?: string[];
    error?: string | null;
    previewAllSelected?: boolean;
    onChange: (fieldId: string, value: string[]) => void;
    validateField: (fieldId: string) => void;
}

const CheckboxCustomization: FC<CheckboxCustomizationProps> = ({
    id,
    label,
    mandatory = false,
    choices,
    error,
    limitations,
    value = [],
    previewAllSelected,
    styles,
    onChange,
}) => {
    const handleChange = useCallback(
        (label: string, checked: boolean) => {
            onChange(id, setChoiceSelected(value, label, checked));
        },
        [onChange, id, value]
    );

    const handleQuantityChange = useCallback(
        (label: string, delta: number) => {
            onChange(id, changeChoiceQuantity(value, label, delta));
        },
        [onChange, id, value]
    );

    const maxChoices = limitations?.max_choices;
    const limitReached = maxChoices != null && value.length >= maxChoices;

    const limitMessage = previewAllSelected
        ? null
        : getChoicesLimitMessage({ limitations, selectedCount: value.length });

    return (
        <Div
            data-auto='product-customizations-checkbox'
            data-grab={CUSTOMIZATIONS_DATA_GRABS.checkboxWrapperDataGrab}
            styles={styles?.checkboxWrapper}
        >
            <BaseProductCustomizationLabel
                label={label}
                required={mandatory}
                styles={mergeResponsiveStyles([
                    labelDefaultStyles,
                    styles?.label,
                ])}
                dataGrab={CUSTOMIZATIONS_DATA_GRABS.labelDataGrab}
                dataAuto='product-customizations-checkbox-label'
            />
            <CheckboxCustomizationChoices
                choices={choices}
                limitations={limitations}
                value={value}
                limitReached={limitReached}
                previewAllSelected={previewAllSelected}
                paidAddonTextStyles={styles?.checkboxPaidAddonText}
                styles={{
                    choicesContainer: styles?.choicesContainer,
                    choiceChecked: styles?.choiceChecked,
                    choice: styles?.choice,
                    choiceLabel: styles?.choiceLabel,
                    choiceQuantityStepper: styles?.choiceQuantityStepper,
                }}
                onChange={handleChange}
                onQuantityChange={handleQuantityChange}
            />

            {error ? (
                <CustomizationFieldError error={error} />
            ) : (
                !!limitMessage && <ChoicesLimitMessage message={limitMessage} />
            )}
        </Div>
    );
};

const labelDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        marginBlockEnd: '8px',
    },
};

export default memo(CheckboxCustomization);
